import * as React from 'react'
import { Language } from 'codegen/types'
import { useLangCookie } from 'cf-i18n/cookies'
import { defaultLanguage } from 'cf-i18n/constants'
import { addOrReplaceLanguageInUrl, getLanguageSegment, validateLocale } from 'cf-i18n/helpers'
import { useUpdateUserLanguageMutation } from 'codegen/generated/user-preferences'
import { useFeatureIsOn } from 'growthbook'
import { localStore } from 'utils/storage'

const STORAGE_KEY = 'cf-navigator-language-selected'

export function useNavigatorLanguageRedirect() {
  const updateUserLanguageMutation = useUpdateUserLanguageMutation()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setLangCookie] = useLangCookie()
  const featureFlag = useFeatureIsOn('GROW-1163-navigator-language')

  React.useEffect(() => {
    const updateLanguage = async () => {
      // We only want to set the language based on the navigator language once
      const hasLanguageBeenSelected = localStore.getString(STORAGE_KEY)
      if (hasLanguageBeenSelected || !featureFlag) {
        return
      }

      const navigatorLanguage = getNavigatorLanguage()
      const currentPageUrl = window.location.href
      const appLanguage = getLanguageSegment(currentPageUrl)

      // If the current language is English and not the same as the navigator language, we need to update the language
      if (
        appLanguage === defaultLanguage &&
        navigatorLanguage &&
        navigatorLanguage !== appLanguage
      ) {
        // We set a flag to prevent this from running again
        localStore.setString(STORAGE_KEY, 'true')

        const newLocation = addOrReplaceLanguageInUrl(currentPageUrl, navigatorLanguage)

        // Update the user language and lang cookie
        setLangCookie(navigatorLanguage)
        try {
          await updateUserLanguageMutation.mutateAsync({ language: navigatorLanguage })
          // oxlint-disable-next-line unicorn/catch-error-name
        } catch (e) {}

        currentPageUrl.includes('staging') &&
          // eslint-disable-next-line no-console
          console.log(
            `🟡 Redirect to: ${{
              to: newLocation,
              from: currentPageUrl,
              trigger: 'use-navigator-language-redirect',
            }}`,
          )
        window.location.href = newLocation
      }
    }
    updateLanguage()
  }, [setLangCookie, updateUserLanguageMutation, featureFlag])

  return null
}

// oxlint-disable-next-line unicorn/prefer-set-has
const SUPPORTED_LANGUAGES = [Language.De]

/**
 * Checks if the language is supported.
 * `navigator.language` can be like "en-US" or "en" so we need to extract the language segment first.
 * @returns The supported language or null if not supported.
 */
function getNavigatorLanguage(): Language | null {
  let navigatorLanguage = navigator.language

  if (navigatorLanguage.includes('-')) {
    navigatorLanguage = navigatorLanguage.split('-')[0]
  }

  if (validateLocale(navigatorLanguage) && SUPPORTED_LANGUAGES.includes(navigatorLanguage)) {
    return navigatorLanguage
  }

  return null
}

// Export this as a component to check if the navigator language is supported before calling the feature flag hook
// This can be removed after GROW-1163 cleanup
export function NavigatorLanguageRedirect() {
  if (typeof window === 'undefined') {
    return null
  }
  const navigatorLanguage = getNavigatorLanguage()
  if (navigatorLanguage) {
    return <Component />
  }
  return null
}

function Component() {
  useNavigatorLanguageRedirect()
  return null
}
